import { ArticleNumberInput } from '@/components/ui/article-number-input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiLanguageInput } from '@/components/ui/multi-language-input';
import { Switch } from '@/components/ui/switch';
import { useCreateInsurance } from '@/hooks/use-insurances';
import { useProject } from '@/hooks/use-projects';
import { getLocalizedText } from '@/lib/utils';
import { createInsuranceSchema, type InsuranceFormData } from '@fame/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';

export function NewInsurancePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { data: project } = useProject(projectId!);
  const createInsurance = useCreateInsurance();

  const form = useForm<InsuranceFormData>({
    resolver: zodResolver(createInsuranceSchema) as Resolver<InsuranceFormData>,
    defaultValues: {
      projectId: projectId!,
      internalName: '',
      description: { nl: '', fr: '', en: '' },
      code: 0,
      articleNumber: '',
      price: 0,
      isActive: true,
    },
  });

  const onSubmit = async (data: InsuranceFormData) => {
    await createInsurance.mutateAsync(data);
    navigate(`/projecten/${projectId}`);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/projecten/${projectId}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Nieuwe Verzekering</h1>
          {project && <p className="text-muted-foreground">{getLocalizedText(project.name)}</p>}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Verzekering Informatie</CardTitle>
          <CardDescription>Voeg een nieuwe verzekeringsoptie toe aan dit project</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="internalName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Interne Naam</FormLabel>
                    <FormControl>
                      <Input placeholder="bijv. Annulatieverzekering" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Beschrijving (voor factuur)</FormLabel>
                    <FormControl>
                      <MultiLanguageInput placeholder="bijv. Annulatieverzekering" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Code</FormLabel>
                      <FormControl>
                        <Input type="number" min="0" step="1" placeholder="bijv. 201" {...field} />
                      </FormControl>
                      <FormDescription>Unieke code voor dit project</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="articleNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Artikelnummer</FormLabel>
                      <FormControl>
                        <ArticleNumberInput value={field.value} onChange={field.onChange} placeholder="bijv. VATE21%" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prijs</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0" placeholder="0,00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Actief</FormLabel>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" asChild>
                  <Link to={`/projecten/${projectId}`}>Annuleren</Link>
                </Button>
                <Button type="submit" disabled={createInsurance.isPending}>
                  {createInsurance.isPending ? 'Bezig met aanmaken...' : 'Verzekering Aanmaken'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
